import {externalLinkTypes} from '@/consts/externalLinkTypes';
import UWM_COMMAND_TYPES from '@/consts/uwm/uwmCommandTypes';
import appErrorService from '@/services/app/appErrorService';
import iccMarketService from '@/services/icc/iccMarketService';
import iccSsoService from '@/services/icc/iccSsoService';
import log from '@/services/logger/log';
import {batchAllowsSessionInfo} from '@/services/sso/ssoIccSessionInfoPolicy';
import {
    resolveSsoLinkUrlForOpenFromState,
    shouldRequestSessionInfo,
} from '@/services/sso/ssoIccSessionInfoPolicy.wired';
import {markSsoLinkUsed} from '@/services/sso/ssoLinkUsage';
import uwmService from '@/services/uwm/uwmService';
import {makeSelectExternalLink} from '@/state/selectors/consumer';
import {selectLanguageCode} from '@/state/selectors/global';
import {makeSelectSsoLink} from '@/state/selectors/media';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch, getState} from '@/state/store';

const fetchAndResolveExternalLinks = async (externalLinks) => {
    if (!externalLinks?.length) return [];

    const externalLinkCodes = [];
    const ssoTemplates = [];
    const urls = [];
    const batchSessionInfoLinks = [];

    externalLinks.forEach((extLink) => {
        addExternalLink(extLink, externalLinkCodes, ssoTemplates, urls, batchSessionInfoLinks);
    });

    for (const externalLinkCode of externalLinkCodes) {
        await iccMarketService.getExternalLink(externalLinkCode);
    }

    await iccSsoService.getSsoLinks({
        ssoTemplates,
        urls,
        includeSessionInfo: shouldRequestSessionInfo({
            allowSessionInfo: batchAllowsSessionInfo(batchSessionInfoLinks),
        }),
    });

    const state = getState();
    const externalLinksResult = [];
    const languageCode = selectLanguageCode(state);

    externalLinks.forEach((extLink) => {
        const {formattedSsoTemplate, externalLink} = extLink || {};
        const isSsoCookiesForced = externalLink?.isSsoCookiesForced ?? false;
        let result;

        if (formattedSsoTemplate) {
            const ssoEntry = makeSelectSsoLink(formattedSsoTemplate)(state);
            const hasSsoSliceData = Boolean(ssoEntry?.status) || Boolean(ssoEntry?.url);

            if (hasSsoSliceData) {
                // UWM links are always opened in a regular tab, never Bluefy
                result = {
                    ...ssoEntry,
                    link: resolveSsoLinkUrlForOpenFromState(ssoEntry, false, isSsoCookiesForced) ?? ssoEntry.url,
                };
            }
        }

        if (!result) {
            result = makeSelectExternalLink(formattedSsoTemplate, languageCode)(state);
        }

        if (!result) return;

        externalLinksResult.push({template: formattedSsoTemplate, externalLinkResult: result});
    });

    return externalLinksResult;
};

const getExternalLinks = async (externalLinks, isRefetch) => {
    try {
        if (!externalLinks?.length) return;

        if (isRefetch) {
            dispatch(showLoader());
        }

        const externalLinksResult = await fetchAndResolveExternalLinks(externalLinks);

        const hasFailedSsoLinks = externalLinksResult.some(({externalLinkResult}) =>
            iccSsoService.isFailed(externalLinkResult?.status)
        );

        if (isRefetch && (externalLinksResult.length !== externalLinks.length || hasFailedSsoLinks)) {
            appErrorService.showGlobalError();
        }

        postExternalLinksResult(externalLinksResult);
    } catch (error) {
        log.info(`Get external link from UWM error ${error}`);
        if (isRefetch) {
            appErrorService.showGlobalError();
        }
    } finally {
        if (isRefetch) {
            dispatch(hideLoader());
        }
    }
};

/**
 * Called when UWM opens a single-use SSO link. Invalidates the consumed token and
 * pushes a fresh link back via UWM_INIT_EXTERNAL_LINKS (no loader).
 */
const onSsoLinkUsed = async (payload) => {
    try {
        const {externalLinks} = payload || {};

        if (!externalLinks?.length) return;

        externalLinks.forEach((extLink) => {
            const {formattedSsoTemplate, externalLink} = extLink || {};

            if (!formattedSsoTemplate) return;

            markSsoLinkUsed({
                ssoLinkKey: formattedSsoTemplate,
                openInBluefy: false,
                isSsoCookiesForced: externalLink?.isSsoCookiesForced ?? false,
            });
        });

        const externalLinksResult = await fetchAndResolveExternalLinks(externalLinks);

        postExternalLinksResult(externalLinksResult);
    } catch (error) {
        log.info(`UWM SSO link used refresh error ${error}`);
    }
};

const addExternalLink = (extLink, externalLinkCodes, ssoTemplates, urls, batchSessionInfoLinks) => {
    const {externalLink, params} = extLink || {};

    if (!externalLink) return;

    const {linkType, ssoTemplate, externalLinkCode, ssoUrl, isSsoCookiesForced = false} = externalLink || {};
    const isExternalLinkCode =
        (externalLinkTypes.isUrl(linkType) || externalLinkTypes.isUrlInApp(linkType)) && externalLinkCode;
    const formattedSsoTemplate = iccSsoService.getFormattedSsoTemplate({ssoTemplate, params});
    const formattedSsoUrl = iccSsoService.getFormattedSsoTemplate({ssoTemplate: ssoUrl, params});
    const sessionInfoFlags = {openInBluefy: false, isSsoCookiesForced};

    if (isExternalLinkCode) {
        externalLinkCodes.push(externalLinkCode);
    } else {
        if (formattedSsoTemplate) {
            ssoTemplates.push(formattedSsoTemplate);
            batchSessionInfoLinks.push(sessionInfoFlags);
        }
        if (formattedSsoUrl) {
            urls.push(formattedSsoUrl);
            batchSessionInfoLinks.push(sessionInfoFlags);
        }
    }
};

const postExternalLinksResult = (externalLinks) => {
    if (!externalLinks?.length) return;

    uwmService.postMessage({
        command: UWM_COMMAND_TYPES.UWM_INIT_EXTERNAL_LINKS,
        payload: {
            externalLinks,
        },
    });
};

export default {
    getExternalLinks,
    onSsoLinkUsed,
};
